import React, { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";

// Fallback list — replaced at runtime by the central-Florida list from
// /api/wishlist/options (which the server returns from `CENTRAL_FLORIDA_CLUBS`).
const FALLBACK_CLUBS = [
  "Bay Hill Club & Lodge",
  "Isleworth Golf & Country Club",
  "Lake Nona Golf & Country Club",
  "Reunion Resort",
  "Bella Collina",
  "Grand Cypress Golf Club",
  "Four Seasons Golf & Sports Club",
  "Other…",
];

/**
 * ClubsEditor — lets a member curate the list of clubs they belong to
 * and their member number at each. Saved via PATCH /api/auth/me/clubs.
 */
export default function ClubsEditor({ user, onSaved }) {
  const [clubs, setClubs] = useState(user.clubs || []);
  const [suggested, setSuggested] = useState(FALLBACK_CLUBS);
  const [addOpen, setAddOpen] = useState((user.clubs || []).length === 0);
  const [select, setSelect] = useState("");
  const [customName, setCustomName] = useState("");
  const [number, setNumber] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [saved, setSaved] = useState(false);
  // Inline edit state — one row at a time.
  const [editIdx, setEditIdx] = useState(-1);
  const [editNumber, setEditNumber] = useState("");

  useEffect(() => {
    setClubs(user.clubs || []);
  }, [user]);

  // Pull the canonical Central Florida club list from the server on mount.
  useEffect(() => {
    let cancelled = false;
    api
      .get("/wishlist/options")
      .then(({ data }) => {
        if (cancelled) return;
        if (Array.isArray(data.clubs) && data.clubs.length > 0) {
          setSuggested([...data.clubs, "Other…"]);
        }
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  const persist = async (nextClubs) => {
    setBusy(true);
    setError("");
    setSaved(false);
    try {
      const { data } = await api.patch("/auth/me/clubs", { clubs: nextClubs });
      setClubs(data.clubs || []);
      onSaved?.(data);
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  const addClub = async () => {
    const chosen = select === "Other…" ? customName.trim() : select.trim();
    if (!chosen || !number.trim()) {
      setError("Pick a club and enter your member number.");
      return;
    }
    // If this is the member's very first club, mark it as the home club by default.
    const isFirst = clubs.length === 0;
    const next = [...clubs, { name: chosen, member_number: number.trim(), is_home: isFirst }];
    await persist(next);
    if (!error) {
      setSelect("");
      setCustomName("");
      setNumber("");
      setAddOpen(false);
    }
  };

  const startEdit = (idx) => {
    setEditIdx(idx);
    setEditNumber(clubs[idx]?.member_number || "");
    setError("");
  };

  const cancelEdit = () => {
    setEditIdx(-1);
    setEditNumber("");
  };

  const saveEdit = async (idx) => {
    const trimmed = editNumber.trim();
    if (!trimmed) {
      setError("Member number cannot be empty.");
      return;
    }
    // Spread the whole prior club object so `club_id`, `slug`, `status`
    // stay attached — the backend also re-hydrates from the DB but this
    // keeps the payload honest.
    const next = clubs.map((c, i) => (i === idx ? { ...c, member_number: trimmed } : c));
    await persist(next);
    if (!error) {
      setEditIdx(-1);
      setEditNumber("");
    }
  };

  const removeClub = async (idx) => {
    const next = clubs.filter((_, i) => i !== idx);
    // If we removed the home club and any others remain, promote the first.
    if (clubs[idx]?.is_home && next.length > 0) {
      next[0] = { ...next[0], is_home: true };
    }
    await persist(next);
  };

  const setHome = async (idx) => {
    const next = clubs.map((c, i) => ({ ...c, is_home: i === idx }));
    await persist(next);
  };

  return (
    <div className="surface-card p-8" data-testid="clubs-editor">
      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="overline">Affiliations</div>
          <h2 className="font-serif text-2xl mt-1 text-[#1A1A1A]">Clubs</h2>
          <p className="text-sm text-[#7A7A7A] mt-1">
            List every club where you hold a membership. The pro shop sees
            these on your orders.
          </p>
        </div>
        {!addOpen && (
          <button
            type="button"
            onClick={() => setAddOpen(true)}
            data-testid="clubs-add-toggle"
            className="btn-secondary text-xs"
          >
            Add Club
          </button>
        )}
      </div>

      {error && (
        <div className="mt-4 px-4 py-3 border border-[#A33] bg-[#fbe9e9] text-[#8a1a1a] text-sm">
          {error}
        </div>
      )}
      {saved && (
        <div className="mt-4 px-4 py-3 border border-[#1A422D] bg-[#E9F0EB] text-[#1A422D] text-sm">
          Saved.
        </div>
      )}

      {/* Existing list */}
      {clubs.length === 0 ? (
        <div className="mt-6 border border-dashed border-[#E5E3D8] p-8 text-center">
          <div className="overline">No clubs yet</div>
          <p className="font-serif text-xl mt-2 text-[#1A1A1A]">
            Add the clubs you belong to.
          </p>
        </div>
      ) : (
        <ul className="mt-6 divide-y divide-[#E5E3D8] border border-[#E5E3D8]" data-testid="clubs-list">
          {clubs.map((c, idx) => (
            <li
              key={`${c.name}-${c.member_number}-${idx}`}
              className="flex items-center justify-between gap-4 px-5 py-3 bg-white"
              data-testid={`club-row-${idx}`}
            >
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <div className="font-serif text-lg text-[#1A1A1A] truncate">{c.name}</div>
                  {c.is_home && (
                    <span
                      data-testid={`club-home-badge-${idx}`}
                      className="px-1.5 py-0.5 text-[9px] tracking-[0.18em] uppercase font-semibold border border-[#B59A5A] text-[#8a6f33] bg-[#FBF6E8]"
                    >
                      Home Club
                    </span>
                  )}
                </div>
                {editIdx === idx ? (
                  <div className="mt-1.5 flex items-center gap-2">
                    <span className="text-xs text-[#7A7A7A] uppercase tracking-wider">Member #</span>
                    <input
                      type="text"
                      value={editNumber}
                      onChange={(e) => setEditNumber(e.target.value)}
                      autoFocus
                      data-testid={`club-edit-number-input-${idx}`}
                      className="input-field h-8 text-sm font-mono max-w-[160px] py-1"
                      onKeyDown={(e) => {
                        if (e.key === "Enter") saveEdit(idx);
                        if (e.key === "Escape") cancelEdit();
                      }}
                    />
                    <button
                      type="button"
                      onClick={() => saveEdit(idx)}
                      disabled={busy}
                      data-testid={`club-edit-save-${idx}`}
                      className="text-xs uppercase tracking-wider text-[#1A422D] hover:underline disabled:opacity-50"
                    >
                      Save
                    </button>
                    <button
                      type="button"
                      onClick={cancelEdit}
                      disabled={busy}
                      data-testid={`club-edit-cancel-${idx}`}
                      className="text-xs uppercase tracking-wider text-[#7A7A7A] hover:underline disabled:opacity-50"
                    >
                      Cancel
                    </button>
                  </div>
                ) : (
                  <div className="text-xs text-[#7A7A7A] uppercase tracking-wider mt-0.5">
                    Member #{" "}
                    <span className="font-mono text-[#1A422D] normal-case tracking-normal">
                      {c.member_number}
                    </span>
                  </div>
                )}
              </div>
              <div className="flex items-center gap-4 flex-shrink-0">
                {editIdx !== idx && (
                  <button
                    type="button"
                    onClick={() => startEdit(idx)}
                    disabled={busy}
                    data-testid={`club-edit-number-${idx}`}
                    className="text-xs uppercase tracking-wider text-[#1A422D] hover:underline disabled:opacity-50"
                  >
                    Edit #
                  </button>
                )}
                {!c.is_home && editIdx !== idx && (
                  <button
                    type="button"
                    onClick={() => setHome(idx)}
                    disabled={busy}
                    data-testid={`club-set-home-${idx}`}
                    className="text-xs uppercase tracking-wider text-[#1A422D] hover:underline disabled:opacity-50"
                  >
                    Set as home
                  </button>
                )}
                {editIdx !== idx && (
                  <button
                    type="button"
                    onClick={() => removeClub(idx)}
                    disabled={busy}
                    data-testid={`club-remove-${idx}`}
                    className="text-xs uppercase tracking-wider text-[#8a1a1a] hover:underline disabled:opacity-50"
                  >
                    Remove
                  </button>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}

      {/* Add form */}
      {addOpen && (
        <div className="mt-6 border border-[#E5E3D8] bg-[#FAF9F2] p-5">
          <div className="overline mb-3">New Club</div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="sm:col-span-2">
              <label className="label-elegant block mb-2">Club</label>
              <select
                value={select}
                onChange={(e) => setSelect(e.target.value)}
                data-testid="clubs-select"
                className="input-field appearance-none"
              >
                <option value="">— Choose a club —</option>
                {suggested.map((c) => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </select>
            </div>
            {select === "Other…" && (
              <div className="sm:col-span-2">
                <label className="label-elegant block mb-2">Club Name</label>
                <input
                  type="text"
                  value={customName}
                  onChange={(e) => setCustomName(e.target.value)}
                  data-testid="clubs-custom-name"
                  className="input-field"
                  placeholder="e.g. Oakridge Golf Club"
                />
              </div>
            )}
            <div className="sm:col-span-2">
              <label className="label-elegant block mb-2">Member Number</label>
              <input
                type="text"
                value={number}
                onChange={(e) => setNumber(e.target.value)}
                data-testid="clubs-member-number"
                className="input-field"
                placeholder="e.g. 0427"
              />
            </div>
          </div>
          <div className="mt-5 flex items-center justify-end gap-3">
            <button
              type="button"
              onClick={() => {
                setAddOpen(false);
                setError("");
                setSelect("");
                setCustomName("");
                setNumber("");
              }}
              className="btn-secondary text-xs"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={addClub}
              disabled={busy}
              data-testid="clubs-save"
              className="btn-primary"
            >
              {busy ? "Saving…" : "Save Club"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
